import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    MenuItem, Snackbar, Tab, Tabs, TextField, Typography, useTheme,
} from "@mui/material";
import {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import PageHeader from "../../components/PageHeader";
import {REP_TYPES} from "../../constants";
import routes from "../../routes";
import {getCurrentUser} from "../../util/AuthUtil";
import {useUserTypes} from "../../hooks/useUserTypes";
import {useUserType} from "../../hooks/useUserType";
import {useUserTypeCreate} from "../../hooks/useUserTypeCreate";
import {useUserTypeUpdate} from "../../hooks/useUserTypeUpdate";
import {useSetUserTypeGroups} from "../../hooks/useSetUserTypeGroups";
import {useAppExperiences} from "../../hooks/useAppExperiences";
import {useGroups} from "../../hooks/useGroups";

// Stable per-app-experience color palette (dot / tinted background / text), assigned to an
// experience by its position in the app-experience list so a given experience keeps its color.
const APP_EXP_PALETTE = [
    {dot: "#495057", bg: "#EDEFF1", fg: "#343A40"},
    {dot: "#4C8DFF", bg: "#EAF2FF", fg: "#1B5FD9"},
    {dot: "#12B886", bg: "#E4F7F0", fg: "#0B7A5A"},
    {dot: "#37B24D", bg: "#E6F5E9", fg: "#2B8A3E"},
    {dot: "#F59F00", bg: "#FFF5E1", fg: "#B27600"},
    {dot: "#7048E8", bg: "#EEE9FB", fg: "#5433C7"},
    {dot: "#E64980", bg: "#FCE8F0", fg: "#C42B63"},
    {dot: "#E8590C", bg: "#FCEBE1", fg: "#C24408"},
];
const NEUTRAL_COLOR = {dot: "#8a8d96", bg: "rgba(0,0,0,0.04)", fg: "rgba(0,0,0,0.6)"};
const BUSINESS_UNITS = ["TM", "CA", "EU", "AU", "KR", "JP"];

const firstBuWithGroups = (availableGroups) => {
    const g = availableGroups ?? [];
    return BUSINESS_UNITS.find((b) => (g.find((a) => a.businessUnitCode === b)?.groups?.length ?? 0) > 0) || "TM";
};

// Human-readable rejection reason carried by a failed request, or "" when the response has none.
// Both save paths fall back to their own wording when it's empty.
const rejectionReason = (error) => {
    const body = error?.response?.data;
    return (body?.message || body?.title || "").trim();
};

const UserTypesPage = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const currentUser = getCurrentUser();

    const [selectedId, setSelectedId] = useState(null);
    const [modalType, setModalType] = useState(null);     // 'details' | 'groups' | null
    const [draft, setDraft] = useState(null);
    const [expFilter, setExpFilter] = useState([]);       // app-experience keys active as list filters
    const [buTab, setBuTab] = useState("TM");             // active BU tab in the view panel
    const [nameError, setNameError] = useState("");
    const [dnError, setDnError] = useState("");
    const [snackbar, setSnackbar] = useState({open: false, severity: "success", message: ""});

    useEffect(() => {
        const route = routes.find((r) => r.key === "user-types");
        if (!route.visibleRepTypes.includes(currentUser.repType.id)) {
            navigate(REP_TYPES[currentUser.repType.id].defaultRoute);
        }
    }, [currentUser, navigate]);

    const [userTypes, refetchUserTypes, listLoading] = useUserTypes();
    const [detail, detailLoading, refetchDetail] = useUserType(selectedId, {enabled: selectedId != null});
    const [appExperiences] = useAppExperiences();
    const [groups] = useGroups();

    const createMutation = useUserTypeCreate();
    const updateMutation = useUserTypeUpdate();
    const setGroupsMutation = useSetUserTypeGroups();

    const accent = theme.palette.secondary.main;
    const showSnackbar = (severity, message) => setSnackbar({open: true, severity, message});
    const closeSnackbar = () => setSnackbar((prev) => ({...prev, open: false}));

    // ------- app-experience lookups (color, order, counts) -------
    const expOrder = useMemo(() => {
        const source = appExperiences.length
            ? appExperiences.map((e) => e.name)
            : Array.from(new Set(userTypes.map((t) => t.appExperience))).sort();
        return source;
    }, [appExperiences, userTypes]);
    const colorByExp = useMemo(() => {
        const m = new Map();
        expOrder.forEach((name, i) => m.set(name, APP_EXP_PALETTE[i % APP_EXP_PALETTE.length]));
        return m;
    }, [expOrder]);
    const colorForExp = (name) => colorByExp.get(name) ?? NEUTRAL_COLOR;
    const expIndex = (name) => { const i = expOrder.indexOf(name); return i < 0 ? 999 : i; };
    const expCount = (name) => userTypes.filter((t) => t.appExperience === name).length;

    // id -> {id, displayName, appExperience}, merged from the list and the selected detail's relations
    const userTypeRefById = useMemo(() => {
        const m = new Map();
        userTypes.forEach((t) => m.set(t.id, {id: t.id, displayName: t.displayName, appExperience: t.appExperience}));
        (detail?.manages ?? []).forEach((t) => { if (!m.has(t.id)) m.set(t.id, t); });
        (detail?.managedBy ?? []).forEach((t) => { if (!m.has(t.id)) m.set(t.id, t); });
        return m;
    }, [userTypes, detail]);

    const groupNameById = useMemo(() => {
        const m = new Map();
        groups.forEach((g) => m.set(g.id, g.name));
        (detail?.availableGroups ?? []).forEach((a) => (a.groups ?? []).forEach((g) => m.set(g.id, g.name)));
        return m;
    }, [groups, detail]);

    const selectedListItem = useMemo(() => userTypes.find((t) => t.id === selectedId) ?? null, [userTypes, selectedId]);

    // keep the view's BU tab on a BU that has groups whenever the detail changes
    useEffect(() => {
        if (detail) setBuTab(firstBuWithGroups(detail.availableGroups));
    }, [detail]);

    const sortRefs = (refs) => [...refs].sort((a, b) =>
        expIndex(a.appExperience) - expIndex(b.appExperience) || (a.displayName ?? "").localeCompare(b.displayName ?? ""));

    const selectUserType = (id) => { setSelectedId(id); };

    // ------------------------------ modals ------------------------------
    const openDetails = (kind) => {
        setNameError(""); setDnError("");
        if (kind === "create") {
            setDraft({kind: "create", name: "", displayName: "", exp: appExperiences[0]?.name ?? "",
                manages: [], mbaseline: [], mexp: "all", msearch: ""});
        } else {
            const src = detail;
            if (!src) return;
            const manageIds = (src.manages ?? []).map((t) => t.id);
            setDraft({kind: "edit", name: src.name, displayName: src.displayName ?? "", exp: src.appExperience ?? "",
                manages: [...manageIds], mbaseline: [...manageIds], mexp: "all", msearch: ""});
        }
        setModalType("details");
    };

    const openGroups = () => {
        if (!detail) return;
        const byBu = {};
        BUSINESS_UNITS.forEach((b) => {
            const cfg = (detail.availableGroups ?? []).find((a) => a.businessUnitCode === b);
            const ids = (cfg?.groups ?? []).map((g) => g.id);
            byBu[b] = {ids: [...ids], def: cfg?.defaultGroupId ?? null, bIds: [...ids]};
        });
        setDraft({bu: firstBuWithGroups(detail.availableGroups), byBu, gsearch: ""});
        setModalType("groups");
    };

    const closeModal = () => { setModalType(null); setDraft(null); setNameError(""); setDnError(""); };

    // ------------------------------ save: details ------------------------------
    const saveDetails = () => {
        setNameError(""); setDnError("");
        const displayName = (draft.displayName ?? "").trim();
        const exp = draft.exp;
        if (draft.kind === "create" && !(draft.name ?? "").trim()) { setNameError("Name is required."); return; }
        if (!displayName) { setDnError("Display name is required."); return; }
        if (!exp) { showSnackbar("error", "App experience is required."); return; }

        if (draft.kind === "create") {
            createMutation.mutate(
                {name: draft.name.trim(), displayName, appExperience: exp, manages: draft.manages},
                {
                    onSuccess: (created) => {
                        showSnackbar("success", "User type created.");
                        refetchUserTypes();
                        setSelectedId(created?.id ?? null);
                        closeModal();
                    },
                    onError: detailsSaveError,
                });
        } else {
            updateMutation.mutate(
                {id: selectedId, payload: {displayName, appExperience: exp, manages: draft.manages}},
                {
                    onSuccess: () => { showSnackbar("success", "Details updated."); refetchUserTypes(); refetchDetail(); closeModal(); },
                    onError: detailsSaveError,
                });
        }
    };

    const detailsSaveError = (error) => {
        const status = error?.response?.status;
        const reason = rejectionReason(error);
        if (status === 409) { setNameError("A user type with this name already exists."); return; }
        if (status === 400) {
            showSnackbar("error", reason || "The app experience or a managed type is no longer valid.");
            return;
        }
        if (status === 404) { showSnackbar("error", "This user type no longer exists."); refetchUserTypes(); closeModal(); return; }
        showSnackbar("error", "Something went wrong. Please try again.");
    };

    // ------------------------------ save: available groups ------------------------------
    const saveGroups = () => {
        const availability = BUSINESS_UNITS.map((b) => ({
            businessUnitCode: b,
            groupIds: draft.byBu[b].ids,
            defaultGroupId: draft.byBu[b].ids.length ? draft.byBu[b].def : null,
        }));
        setGroupsMutation.mutate({userTypeId: selectedId, availability}, {
            onSuccess: () => { showSnackbar("success", "Available groups updated."); refetchDetail(); refetchUserTypes(); closeModal(); },
            onError: (error) => {
                const status = error?.response?.status;
                const reason = rejectionReason(error);
                if (status === 400) {
                    showSnackbar("error", reason || "One or more entries were rejected. No changes applied.");
                }
                else if (status === 404) { showSnackbar("error", "This user type no longer exists."); refetchUserTypes(); closeModal(); }
                else showSnackbar("error", "Something went wrong. Please try again.");
            },
        });
    };

    // ------------------------------ list ------------------------------
    const labelSx = {fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mb: 0.5};

    const expBadge = (name, {small} = {}) => {
        const col = colorForExp(name);
        return (
            <Box component="span" sx={{display: "inline-flex", alignItems: "center", gap: 0.6, px: small ? 1 : 1.1, py: 0.2,
                borderRadius: "999px", border: `1px solid ${col.dot}`, backgroundColor: col.bg, color: col.fg,
                fontSize: small ? "0.62rem" : "0.7rem", fontWeight: 700, letterSpacing: "0.03em"}}>
                <Box sx={{width: 7, height: 7, borderRadius: "50%", backgroundColor: col.dot}} />
                {name}
            </Box>
        );
    };

    const toggleExpFilter = (name) => setExpFilter((f) => f.includes(name) ? f.filter((x) => x !== name) : [...f, name]);

    const renderList = () => {
        const filtering = expFilter.length > 0;
        const shown = userTypes.filter((t) => !filtering || expFilter.includes(t.appExperience));
        const legendExps = expOrder.filter((name) => expCount(name) > 0);
        return (
            <Box sx={{borderRight: `1px solid ${theme.palette.divider}`, display: "flex", flexDirection: "column", minHeight: 0, backgroundColor: "white"}}>
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, borderBottom: `1px solid ${theme.palette.divider}`}}>
                    <Typography variant="body1" sx={{fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "text.secondary"}}>
                        User Types ({filtering ? `${shown.length} of ${userTypes.length}` : userTypes.length})
                    </Typography>
                    <Button size="small" onClick={() => openDetails("create")} sx={{fontWeight: 700}}>+ New</Button>
                </Box>
                <Box sx={{p: 2, borderBottom: `1px solid ${theme.palette.divider}`}}>
                    <Typography sx={{...labelSx, mb: 1}}>App Experiences</Typography>
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75}}>
                        {legendExps.map((name) => {
                            const col = colorForExp(name);
                            const active = expFilter.includes(name);
                            return (
                                <Box key={name} onClick={() => toggleExpFilter(name)}
                                     sx={{display: "inline-flex", alignItems: "center", gap: 0.6, px: 1.1, py: 0.4, borderRadius: "999px", cursor: "pointer",
                                         border: `1px solid ${active ? col.dot : theme.palette.divider}`, backgroundColor: active ? col.bg : "white",
                                         fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.03em", opacity: filtering && !active ? 0.35 : 1}}>
                                    <Box sx={{width: 8, height: 8, borderRadius: "50%", backgroundColor: col.dot}} />
                                    {name}
                                    <span style={{color: theme.palette.text.secondary}}>{expCount(name)}</span>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
                <Box sx={{flex: 1, overflowY: "auto", minHeight: 0}}>
                    {listLoading && userTypes.length === 0 ? (
                        <Box sx={{display: "flex", justifyContent: "center", p: 4}}><CircularProgress size={24} /></Box>
                    ) : shown.map((t) => {
                        const sel = t.id === selectedId;
                        const col = colorForExp(t.appExperience);
                        const meta = [];
                        if (t.managesCount > 0) meta.push(`Manages ${t.managesCount}`);
                        if (t.managedByCount > 0) meta.push(`Managed by ${t.managedByCount}`);
                        return (
                            <Box key={t.id} onClick={() => selectUserType(t.id)}
                                 sx={{display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.25, cursor: "pointer",
                                     borderBottom: `1px solid ${theme.palette.divider}`, borderLeft: `3px solid ${sel ? accent : "transparent"}`,
                                     backgroundColor: sel ? "rgba(0, 163, 255, 0.12)" : "transparent",
                                     "&:hover": {backgroundColor: sel ? "rgba(0, 163, 255, 0.12)" : theme.palette.background.secondary}}}>
                                <Box sx={{flex: "0 0 34px", height: 34, borderRadius: "4px", border: `1px solid ${col.dot}`,
                                    backgroundColor: col.bg, color: col.fg, display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: "0.72rem", fontWeight: 700}} title={t.appExperience}>
                                    {t.id}
                                </Box>
                                <Box sx={{minWidth: 0, flex: 1}}>
                                    <Typography noWrap sx={{fontWeight: 500}}>{t.displayName}</Typography>
                                    <Box sx={{display: "flex", alignItems: "center", gap: 1, color: "text.secondary", fontSize: "0.78rem"}}>
                                        {meta.length ? meta.map((m, i) => (
                                            <span key={i}>{i > 0 && <span style={{opacity: 0.4, marginRight: 8}}>&middot;</span>}{m}</span>
                                        )) : <span>—</span>}
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        );
    };

    // ------------------------------ read-only detail ------------------------------
    const sectionCard = (title, onEdit, body) => (
        <Box sx={{border: `1px solid ${theme.palette.divider}`, borderRadius: "8px", mb: 2, overflow: "hidden"}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25, borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.secondary}}>
                <Typography sx={{fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "text.primary"}}>{title}</Typography>
                {onEdit && <Button size="small" onClick={onEdit} sx={{ml: "auto", fontWeight: 700}}>Edit</Button>}
            </Box>
            <Box sx={{p: 2}}>{body}</Box>
        </Box>
    );

    const neutralChips = (refs) => (
        refs.length
            ? <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75}}>
                {refs.map((r) => (
                    <Box key={r.id} component="span" sx={{display: "inline-flex", alignItems: "center", px: 1.25, py: 0.4, borderRadius: "999px",
                        fontSize: "0.78rem", fontWeight: 500, backgroundColor: theme.palette.background.secondary, border: `1px solid ${theme.palette.divider}`}}>
                        {r.displayName}
                    </Box>
                ))}
            </Box>
            : <Typography sx={{color: "text.disabled", fontStyle: "italic"}}>None</Typography>
    );

    const renderAvailableGroupsView = () => {
        const available = detail?.availableGroups ?? [];
        const cfg = available.find((a) => a.businessUnitCode === buTab);
        const buGroups = [...(cfg?.groups ?? [])].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
        return (
            <>
                <Tabs value={buTab} onChange={(e, v) => setBuTab(v)} variant="scrollable" scrollButtons={false}
                      sx={{minHeight: 0, mb: 2, borderBottom: `1px solid ${theme.palette.divider}`, "& .MuiTab-root": {minHeight: 0, py: 1, textTransform: "none", fontWeight: 600}}}>
                    {BUSINESS_UNITS.map((b) => {
                        const n = (available.find((a) => a.businessUnitCode === b)?.groups?.length) ?? 0;
                        return <Tab key={b} value={b} label={`${b} (${n})`} />;
                    })}
                </Tabs>
                {buGroups.length ? (
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75}}>
                        {buGroups.map((g) => {
                            const isDefault = g.id === cfg?.defaultGroupId;
                            return (
                                <Box key={g.id} sx={{display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.4, borderRadius: "999px",
                                    fontSize: "0.8rem", fontWeight: 500,
                                    border: `1px solid ${isDefault ? accent : theme.palette.divider}`,
                                    backgroundColor: isDefault ? "rgba(0, 163, 255, 0.12)" : theme.palette.background.secondary}}>
                                    <span>{g.name}</span>
                                    {isDefault && <Box component="span" sx={{fontSize: "0.62rem", fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: accent}}>★ Default</Box>}
                                </Box>
                            );
                        })}
                    </Box>
                ) : <Typography sx={{color: "text.secondary", fontStyle: "italic", fontSize: "0.85rem"}}>No groups configured for {buTab}.</Typography>}
            </>
        );
    };

    const renderDetail = () => {
        if (selectedId == null) {
            return <Box sx={{flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary", p: 5}}><Typography sx={{fontStyle: "italic"}}>Select a user type to view details</Typography></Box>;
        }
        if (detailLoading && !detail) {
            return <Box sx={{display: "flex", justifyContent: "center", p: 5}}><CircularProgress size={28} /></Box>;
        }
        if (!detail) return null;
        const manages = sortRefs(detail.manages ?? []);
        const managedBy = sortRefs(detail.managedBy ?? []);
        const totalGroups = (detail.availableGroups ?? []).reduce((n, a) => n + (a.groups?.length ?? 0), 0);
        const busWithGroups = (detail.availableGroups ?? []).filter((a) => (a.groups?.length ?? 0) > 0).length;
        return (
            <Box sx={{p: 4}}>
                <Box sx={{mb: 3}}>
                    <Typography variant="h6" sx={{fontWeight: 500}}>{detail.displayName}</Typography>
                    <Typography sx={{color: "text.secondary", fontSize: "0.85rem", mt: 0.5}}>
                        {totalGroups} group{totalGroups !== 1 ? "s" : ""} across {busWithGroups} BU{busWithGroups !== 1 ? "s" : ""} &middot; Manages {manages.length} type{manages.length !== 1 ? "s" : ""}
                    </Typography>
                </Box>

                {sectionCard("Details", () => openDetails("edit"), (
                    <Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2}}>
                        <Box><Typography sx={labelSx}>ID</Typography><Typography sx={{fontFamily: "monospace"}}>{detail.id}</Typography></Box>
                        <Box><Typography sx={labelSx}>App Experience</Typography><Typography>{detail.appExperience}</Typography></Box>
                        <Box><Typography sx={labelSx}>Name</Typography><Typography sx={{fontFamily: "monospace"}}>{detail.name}</Typography></Box>
                        <Box><Typography sx={labelSx}>Display Name</Typography><Typography>{detail.displayName}</Typography></Box>
                        <Box sx={{gridColumn: "1 / -1"}}><Typography sx={labelSx}>Managed User Types</Typography>{neutralChips(manages)}</Box>
                        <Box sx={{gridColumn: "1 / -1"}}><Typography sx={labelSx}>Managed By</Typography>{neutralChips(managedBy)}</Box>
                    </Box>
                ))}

                {sectionCard(`Available Groups (${totalGroups})`, openGroups, renderAvailableGroupsView())}
            </Box>
        );
    };

    // ------------------------------ details modal (create/edit + managed-types editor) ------------------------------
    const renderManagedEditor = () => {
        const selfId = draft.kind === "edit" ? selectedId : null;
        const candidates = userTypes.filter((t) => t.id !== selfId);
        const staged = new Set(draft.manages);
        const baseline = new Set(draft.mbaseline);
        const filtering = draft.mexp !== "all";

        const selInExp = (name) => draft.manages.filter((id) => userTypeRefById.get(id)?.appExperience === name).length;
        const availInExp = (name) => candidates.filter((t) => t.appExperience === name && !staged.has(t.id) && !baseline.has(t.id)).length;
        const availAll = candidates.filter((t) => !staged.has(t.id) && !baseline.has(t.id)).length;

        const toggleManage = (id) => setDraft((d) => ({...d, manages: d.manages.includes(id) ? d.manages.filter((x) => x !== id) : [...d.manages, id]}));
        const setMexp = (v) => setDraft((d) => ({...d, mexp: v}));

        const unionIds = Array.from(new Set([...draft.mbaseline, ...draft.manages]));
        let unionRefs = sortRefs(unionIds.map((id) => userTypeRefById.get(id)).filter(Boolean));
        if (filtering) unionRefs = unionRefs.filter((r) => r.appExperience === draft.mexp);

        const q = (draft.msearch ?? "").trim().toLowerCase();
        let available = candidates.filter((t) => !staged.has(t.id) && !baseline.has(t.id));
        if (filtering) available = available.filter((t) => t.appExperience === draft.mexp);
        if (q) available = available.filter((t) => (t.displayName ?? "").toLowerCase().includes(q) || (t.name ?? "").toLowerCase().includes(q));
        available = sortRefs(available.map((t) => ({id: t.id, displayName: t.displayName, appExperience: t.appExperience, name: t.name})));

        const legendExps = expOrder.filter((name) => expCount(name) > 0);

        return (
            <>
                <Typography sx={{...labelSx, mt: 1}}>App Experiences</Typography>
                <Box sx={{display: "flex", flexWrap: "wrap", gap: 1, mb: 2}}>
                    {legendExps.map((name) => {
                        const col = colorForExp(name);
                        const active = draft.mexp === name;
                        return (
                            <Box key={name} onClick={() => setMexp(active ? "all" : name)}
                                 sx={{display: "inline-flex", alignItems: "center", gap: 0.6, px: 1.1, py: 0.4, borderRadius: "999px", cursor: "pointer",
                                     border: `1px solid ${active ? col.dot : theme.palette.divider}`, backgroundColor: active ? col.bg : "white",
                                     fontSize: "0.7rem", fontWeight: 700, opacity: filtering && !active ? 0.35 : 1}}>
                                <Box sx={{width: 8, height: 8, borderRadius: "50%", backgroundColor: col.dot}} />
                                {name}<span style={{color: theme.palette.text.secondary}}>{selInExp(name)} of {expCount(name)}</span>
                            </Box>
                        );
                    })}
                </Box>

                <Typography sx={labelSx}>Managed User Types ({draft.manages.length})</Typography>
                <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75, minHeight: 30, mb: 2}}>
                    {unionRefs.length ? unionRefs.map((r) => {
                        const inD = staged.has(r.id), inB = baseline.has(r.id);
                        const state = inD && !inB ? "added" : (!inD && inB ? "removed" : undefined);
                        const col = colorForExp(r.appExperience);
                        return (
                            <Box key={r.id} onClick={() => toggleManage(r.id)}
                                 sx={{display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.4, borderRadius: "999px", cursor: "pointer",
                                     border: `${state === "added" ? 2 : 1}px ${state === "removed" ? "dashed" : "solid"} ${col.dot}`,
                                     backgroundColor: col.bg, color: col.fg, fontSize: "0.8rem", fontWeight: state === "added" ? 700 : 500,
                                     opacity: state === "removed" ? 0.5 : 1, textDecoration: state === "removed" ? "line-through" : "none"}}>
                                <Box sx={{width: 8, height: 8, borderRadius: "50%", backgroundColor: col.dot}} />
                                {state === "added" && <span>+</span>}
                                <span>{r.displayName}</span>
                                <span style={{opacity: 0.6, fontSize: "0.95rem", lineHeight: 1}}>{state === "removed" ? "↺" : "×"}</span>
                            </Box>
                        );
                    }) : <Typography sx={{color: "text.secondary", fontStyle: "italic", fontSize: "0.82rem"}}>{filtering ? "None in this experience" : "No managed types"}</Typography>}
                </Box>

                <Box sx={{display: "flex", gap: 1, mb: 1}}>
                    <TextField size="small" select value={draft.mexp} onChange={(e) => setMexp(e.target.value)} sx={{flex: "0 0 210px"}}>
                        <MenuItem value="all">All experiences ({availAll})</MenuItem>
                        {legendExps.map((name) => <MenuItem key={name} value={name}>{name} ({availInExp(name)})</MenuItem>)}
                    </TextField>
                    <TextField size="small" fullWidth placeholder="Search user types to add…" value={draft.msearch}
                               onChange={(e) => setDraft((d) => ({...d, msearch: e.target.value}))} />
                </Box>
                <Typography sx={labelSx}>Available</Typography>
                <Box sx={{maxHeight: "28vh", overflowY: "auto", border: `1px solid ${theme.palette.divider}`, borderRadius: "4px"}}>
                    {available.length ? available.map((r) => {
                        const col = colorForExp(r.appExperience);
                        return (
                            <Box key={r.id} onClick={() => toggleManage(r.id)}
                                 sx={{display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 1, cursor: "pointer",
                                     borderBottom: `1px solid ${theme.palette.divider}`, "&:hover": {backgroundColor: theme.palette.background.secondary}}}>
                                <Box sx={{width: 9, height: 9, borderRadius: "50%", backgroundColor: col.dot, flex: "0 0 9px"}} />
                                <Typography sx={{flex: 1, fontSize: "0.875rem"}}>{r.displayName}
                                    <Typography component="span" sx={{fontSize: "0.68rem", color: "text.secondary"}}> · {r.appExperience}</Typography>
                                </Typography>
                                <Typography sx={{fontSize: "1.05rem", color: accent, fontWeight: 700, width: 18, textAlign: "center"}}>+</Typography>
                            </Box>
                        );
                    }) : <Typography sx={{p: 1.5, fontSize: "0.85rem", color: "text.secondary", fontStyle: "italic"}}>No matching user types</Typography>}
                </Box>
            </>
        );
    };

    const renderDetailsModal = () => {
        const isCreate = draft.kind === "create";
        const managedByRefs = sortRefs(detail?.managedBy ?? []);
        return (
            <>
                <DialogContent dividers>
                    {isCreate ? (
                        <TextField label="Name" size="small" fullWidth required value={draft.name}
                                   error={!!nameError} helperText={nameError || "Internal key — cannot be changed later."}
                                   onChange={(e) => { setNameError(""); setDraft((d) => ({...d, name: e.target.value})); }} />
                    ) : (
                        <Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, pb: 2, mb: 1, borderBottom: `1px solid ${theme.palette.divider}`}}>
                            <Box><Typography sx={labelSx}>ID</Typography><Typography sx={{fontFamily: "monospace"}}>{detail?.id}</Typography></Box>
                            <Box><Typography sx={labelSx}>Name</Typography><Typography sx={{fontFamily: "monospace"}}>{detail?.name}</Typography></Box>
                            <Box sx={{gridColumn: "1 / -1"}}><Typography sx={labelSx}>Managed By</Typography>{neutralChips(managedByRefs)}</Box>
                        </Box>
                    )}
                    <TextField label="Display Name" size="small" fullWidth required sx={{mt: isCreate ? 1 : 0}} value={draft.displayName}
                               error={!!dnError} helperText={dnError || " "}
                               onChange={(e) => { setDnError(""); setDraft((d) => ({...d, displayName: e.target.value})); }} />
                    <TextField label="App Experience" size="small" select fullWidth required value={draft.exp}
                               onChange={(e) => setDraft((d) => ({...d, exp: e.target.value}))}>
                        {appExperiences.map((e) => <MenuItem key={e.name} value={e.name}>{e.name}</MenuItem>)}
                    </TextField>
                    {renderManagedEditor()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeModal}>Cancel</Button>
                    <Button variant="contained" onClick={saveDetails} disabled={createMutation.isPending || updateMutation.isPending}>{isCreate ? "Create" : "Save"}</Button>
                </DialogActions>
            </>
        );
    };

    // ------------------------------ available-groups modal ------------------------------
    const renderGroupsModal = () => {
        const cfg = draft.byBu[draft.bu];
        const staged = new Set(cfg.ids);
        const baseline = new Set(cfg.bIds);

        const setGroupBu = (b) => setDraft((d) => ({...d, bu: b}));
        const toggleGroup = (id) => setDraft((d) => {
            const c = {...d.byBu[d.bu]};
            const ids = c.ids.includes(id) ? c.ids.filter((x) => x !== id) : [...c.ids, id];
            let def = c.def;
            if (c.ids.includes(id)) { if (def === id) def = ids[0] ?? null; }   // removed
            else if (def == null) def = id;                                     // first add auto-default
            return {...d, byBu: {...d.byBu, [d.bu]: {...c, ids, def}}};
        });
        const setDefault = (id) => setDraft((d) => {
            const c = d.byBu[d.bu];
            if (!c.ids.includes(id)) return d;
            return {...d, byBu: {...d.byBu, [d.bu]: {...c, def: id}}};
        });

        const unionIds = Array.from(new Set([...cfg.bIds, ...cfg.ids]));
        const unionGroups = unionIds
            .map((id) => ({id, name: groupNameById.get(id)}))
            .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));

        const q = (draft.gsearch ?? "").trim().toLowerCase();
        let available = groups.filter((g) => !staged.has(g.id) && !baseline.has(g.id));
        if (q) available = available.filter((g) => (g.name ?? "").toLowerCase().includes(q));
        available = [...available].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));

        const defName = groupNameById.get(cfg.def);

        return (
            <>
                <DialogContent dividers>
                    <Tabs value={draft.bu} onChange={(e, v) => setGroupBu(v)} variant="scrollable" scrollButtons={false}
                          sx={{minHeight: 0, mb: 2, borderBottom: `1px solid ${theme.palette.divider}`, "& .MuiTab-root": {minHeight: 0, py: 1, textTransform: "none", fontWeight: 600}}}>
                        {BUSINESS_UNITS.map((b) => <Tab key={b} value={b} label={`${b} (${draft.byBu[b].ids.length})`} />)}
                    </Tabs>

                    <Typography sx={labelSx}>Groups Available in {draft.bu} ({cfg.ids.length})</Typography>
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75, minHeight: 30, mb: 1}}>
                        {unionGroups.length ? unionGroups.map((g) => {
                            const inD = staged.has(g.id), inB = baseline.has(g.id);
                            const state = inD && !inB ? "added" : (!inD && inB ? "removed" : undefined);
                            const isDefault = inD && cfg.def === g.id;
                            const border = state === "added" ? accent : theme.palette.divider;
                            return (
                                <Box key={g.id} onClick={() => toggleGroup(g.id)}
                                     sx={{display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.4, borderRadius: "999px", cursor: "pointer",
                                         border: `${state === "added" ? 2 : 1}px ${state === "removed" ? "dashed" : "solid"} ${border}`,
                                         backgroundColor: state === "added" ? "rgba(0, 163, 255, 0.12)" : theme.palette.background.secondary,
                                         opacity: state === "removed" ? 0.5 : 1, fontSize: "0.8rem", fontWeight: 500,
                                         textDecoration: state === "removed" ? "line-through" : "none"}}>
                                    {state === "added" && <span>+</span>}
                                    <span>{g.name}</span>
                                    {inD && (
                                        <Box component="span" onClick={(e) => { e.stopPropagation(); setDefault(g.id); }}
                                             title={isDefault ? `Default for ${draft.bu}` : "Set as default"}
                                             sx={{fontSize: "0.62rem", fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase",
                                                 color: isDefault ? accent : "text.disabled", cursor: "pointer", px: 0.5, borderRadius: "999px",
                                                 "&:hover": {color: accent}}}>
                                            {isDefault ? "★ Default" : "☆"}
                                        </Box>
                                    )}
                                    <span style={{opacity: 0.6, fontSize: "0.95rem", lineHeight: 1}}>{state === "removed" ? "↺" : "×"}</span>
                                </Box>
                            );
                        }) : <Typography sx={{color: "text.secondary", fontStyle: "italic", fontSize: "0.82rem"}}>No groups selected for {draft.bu}</Typography>}
                    </Box>
                    <Typography sx={{fontSize: "0.72rem", color: "text.secondary", mb: 1.5}}>
                        {cfg.ids.length ? <>{cfg.ids.length} group{cfg.ids.length !== 1 ? "s" : ""} in {draft.bu} · default <b>{defName ?? "—"}</b> · click ☆ on a pill to change</> : `No groups selected for ${draft.bu}.`}
                    </Typography>

                    <TextField size="small" fullWidth placeholder="Search groups to add…" value={draft.gsearch}
                               onChange={(e) => setDraft((d) => ({...d, gsearch: e.target.value}))} sx={{mb: 1}} />
                    <Typography sx={labelSx}>Available</Typography>
                    <Box sx={{maxHeight: "30vh", overflowY: "auto", border: `1px solid ${theme.palette.divider}`, borderRadius: "4px"}}>
                        {available.length ? available.map((g) => (
                            <Box key={g.id} onClick={() => toggleGroup(g.id)}
                                 sx={{display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, py: 1, cursor: "pointer",
                                     borderBottom: `1px solid ${theme.palette.divider}`, "&:hover": {backgroundColor: theme.palette.background.secondary}}}>
                                <Typography sx={{fontSize: "0.875rem"}}>{g.name}</Typography>
                                <Typography sx={{fontSize: "1.05rem", color: accent, fontWeight: 700}}>+</Typography>
                            </Box>
                        )) : <Typography sx={{p: 1.5, fontSize: "0.85rem", color: "text.secondary", fontStyle: "italic"}}>No matching groups</Typography>}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeModal}>Cancel</Button>
                    <Button variant="contained" onClick={saveGroups} disabled={setGroupsMutation.isPending}>Save</Button>
                </DialogActions>
            </>
        );
    };

    const renderModal = () => {
        if (!modalType) return null;
        const title = modalType === "groups"
            ? `Edit Available Groups for ${detail?.displayName ?? ""}`
            : (draft.kind === "create" ? "New User Type" : `Edit Details for ${detail?.displayName ?? ""}`);
        return (
            <Dialog open onClose={closeModal} maxWidth="sm" fullWidth>
                <DialogTitle>{title}</DialogTitle>
                {modalType === "details" ? renderDetailsModal() : renderGroupsModal()}
            </Dialog>
        );
    };

    return (
        <Box sx={{width: "100%", height: "100%", display: "flex", flexDirection: "column"}}>
            <PageHeader title="Manage User Types" />
            <Box sx={{flex: 1, minHeight: 0, p: 3, display: "flex"}}>
                <Box sx={{flex: 1, display: "grid", gridTemplateColumns: "320px 1fr", border: `1px solid ${theme.palette.divider}`, borderRadius: "4px", overflow: "hidden", minHeight: 0}}>
                    {renderList()}
                    <Box sx={{display: "flex", flexDirection: "column", minHeight: 0, overflowY: "auto", backgroundColor: "white"}}>{renderDetail()}</Box>
                </Box>
            </Box>

            {renderModal()}

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar} anchorOrigin={{vertical: "bottom", horizontal: "center"}}>
                <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{width: "100%"}}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default UserTypesPage;
